import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useArchivedItems,
  useRestoreItem,
  useImportCsv,
  ExpiryItem,
} from "../hooks/useExpiryItems";
import { format, parseISO } from "date-fns";
import toast from "react-hot-toast";
import Header from "../components/Header";
import ImportModal from "../components/ImportModal";
import SettingsModal from "../components/SettingsModal";
import SearchBar from "../components/SearchBar";
import DataTable from "../components/DataTable";
import AvatarGroup from "../components/AvatarGroup";
import { ArchiveIcon, RestoreIcon } from "../components/Icons";
import { TableColumn } from "../types";

const ArchivedItems: React.FC = () => {
  const navigate = useNavigate();
  const { data: items = [], isLoading } = useArchivedItems();
  const restoreMutation = useRestoreItem();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const importCsvMutation = useImportCsv();

  const handleImport = useCallback(
    async (csvText: string) => {
      await importCsvMutation.mutateAsync(csvText);
    },
    [importCsvMutation]
  );

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  const handleRestore = async (item: ExpiryItem) => {
    try {
      await restoreMutation.mutateAsync(item.id);
      toast.success(`"${item.name}" restored to active documents`);
    } catch {
      toast.error("Failed to restore item");
    }
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns: TableColumn<ExpiryItem>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Document",
        sortable: true,
        render: (_value, row) => (
          <div className="flex items-center">
            <div className="flex-shrink-0 h-8 w-8 bg-[#F3F4F6] rounded flex items-center justify-center mr-3">
              <svg
                className="h-4 w-4 text-[#9CA3AF]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-[#6B7280] line-through">
                {row.name}
              </p>
              <p className="text-xs text-[#9CA3AF]">{row.category}</p>
            </div>
          </div>
        ),
      },
      {
        key: "category",
        label: "Category",
        sortable: true,
        render: (value) => (
          <span className="text-[#9CA3AF]">{(value as string) || "—"}</span>
        ),
      },
      {
        key: "expiry_date",
        label: "Expiry Date",
        sortable: true,
        render: (value) => (
          <span className="text-[#9CA3AF]">
            {value ? format(parseISO(value as string), "MMM dd, yyyy") : "—"}
          </span>
        ),
      },
      {
        key: "archived_at" as keyof ExpiryItem,
        label: "Archived On",
        sortable: true,
        render: (_value, row) => {
          const at = (row as any).archived_at;
          return (
            <span className="text-xs text-[#9CA3AF]">
              {at ? format(parseISO(at), "MMM dd, yyyy") : "—"}
            </span>
          );
        },
      },
      {
        key: "owner",
        label: "Owner",
        render: (_, row) => {
          const owners = (row as any).owners || [];
          if (owners.length === 0)
            return <span className="text-[#9CA3AF]">—</span>;
          return (
            <AvatarGroup
              avatars={owners.map((o: any) => ({
                src: o.avatar,
                name: o.name || o.email,
                alt: o.name || o.email,
              }))}
            />
          );
        },
      },
      {
        key: "restore",
        label: "",
        render: (_, row) => (
          <button
            onClick={() => handleRestore(row)}
            disabled={restoreMutation.isLoading}
            title="Restore to active documents"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-[#E5E7EB] text-[#374151] hover:bg-[#F0FDF4] hover:text-[#15803D] hover:border-[#86EFAC] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RestoreIcon className="h-3.5 w-3.5" />
            Restore
          </button>
        ),
      },
    ],
    [restoreMutation.isLoading]
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <>
        <Header
          onImport={() => setIsImportOpen(true)}
          onSettings={() => setIsSettingsOpen(true)}
          onOpenPricing={() => navigate("/pricing")}
        />
        <div className="bg-[#F9FAFB] min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563EB]" />
        </div>
      </>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <>
      <Header
        onImport={() => setIsImportOpen(true)}
        onSettings={() => setIsSettingsOpen(true)}
        onOpenPricing={() => navigate("/pricing")}
      />

      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}
      {isImportOpen && (
        <ImportModal
          onClose={() => setIsImportOpen(false)}
          onImport={handleImport}
        />
      )}

      <div className="bg-[#F9FAFB] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* ── Page title ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 mb-5">
            <div className="h-9 w-9 bg-[#F3F4F6] rounded-lg flex items-center justify-center">
              <ArchiveIcon className="h-5 w-5 text-[#6B7280]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#111827]">Archive</h2>
              <p className="text-sm text-[#6B7280]">
                {items.length} archived document{items.length !== 1 ? "s" : ""}{" "}
                — restore any time
              </p>
            </div>
          </div>

          {/* ── Search ──────────────────────────────────────────────────── */}
          <div className="bg-white rounded-t-lg border border-[#E5E7EB] shadow-sm">
            <div className="flex items-center px-4 py-3 space-x-3">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search archived documents…"
              />
            </div>
          </div>

          {/* ── Table ───────────────────────────────────────────────────── */}
          <div className="bg-white rounded-b-lg border border-t-0 border-[#E5E7EB] shadow-sm overflow-hidden">
            <DataTable
              columns={columns}
              data={filteredItems}
              getRowId={(row) => row.id}
              emptyMessage={
                searchQuery
                  ? "No archived documents match your search."
                  : "No archived documents yet. Archive items from the dots menu in the main table."
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ArchivedItems;
